import {
  createUserWithEmailAndPassword,
  getIdTokenResult,
  signOut,
  User,
  UserCredential,
} from "firebase/auth";
import { doc, Timestamp, writeBatch } from "firebase/firestore";
import { auth, firestore } from "../firebase";
import { getTimestampForTPlus } from "../lib/get-timestamp-for-t-plus";
import { debugLogError } from "../utils/debug-log-error";
import { logUserActivity, UserActivityEventType } from "./activityLogger";

interface RegisterError {
  code: string;
  message: string;
}

export const registerUser = async (
  email: string,
  password: string
): Promise<User | RegisterError> => {
  try {
    const userCredential: UserCredential = await createUserWithEmailAndPassword(
      auth,
      email,
      password
    );
    const user = userCredential.user;

    const batch = writeBatch(firestore);
    batch.set(doc(firestore, "users", user.uid), {
      uid: user.uid,
      email: user.email,
      onboardingCompleted: false,
      createdAt: new Date().getTime(),
      usernameUpdateCount: 0,
      nextUsernameUpdateAt: getTimestampForTPlus(0),
    });
    batch.set(doc(firestore, "profiles", user.uid), {
      userId: user.uid,
      createdAt: Timestamp.now(),
    });
    await batch.commit();

    logUserActivity({
      userId: user.uid,
      eventType: UserActivityEventType.USER_REGISTERED,
      timestamp: Date.now(),
      context: {
        email: user.email,
      },
    });

    return user;
  } catch (error: any) {
    await signOut(auth);
    debugLogError("registerUser", error);
    return {
      code: error.code,
      message: error.message,
    };
  }
};

export const isAdmin = async (
  user: User | null | undefined
): Promise<boolean> => {
  if (!user) {
    return false;
  }
  try {
    const idTokenResult = await getIdTokenResult(user);
    return idTokenResult.claims.admin === true;
  } catch (error) {
    console.error("Error getting ID token result:", error);
    return false;
  }
};
